import {
  Component, ViewEncapsulation, ChangeDetectionStrategy,
  Output, EventEmitter, Input, Inject, OnInit
} from '@angular/core';
import { UntypedFormGroup, UntypedFormControl, Validators } from '@angular/forms';
import { DatePipe } from '@angular/common';
import { take, tap, switchMap } from 'rxjs/operators';

import { AlertSelectParams } from '@shared/widgets/entities/alerts';
import { DateTransformPipe } from '@app/pipes/date-format.pipe';
import { FlightType } from '@search-bar/entities/flight-type';
import { FormValidationService } from '@reservation/services/form-validation.service';
import { IS_MOBILE } from '@app/modules/core/tokens/app.tokens';
import { AVO_EVENTS } from '@avo/events/avo.events';
import { AvoService } from '@avo/services/avo.service';
import { AvoDispatchOrchestrator } from '@avo/orchestrator/avo.dispatch';
import { PropertiesAmplitudeService } from '@app/services/properties-amplitude.service';
import { SearchBy } from '@shared/base-search-bar/entities/search.entities';
import { CheckboxAlign } from '@design-system/components/organisms/checkbox/entities/checkbox.types';

@Component({
  selector: 'app-alert-form',
  templateUrl: './alert-form.component.html',
  styleUrls: ['./alert-form.component.sass'],
  encapsulation: ViewEncapsulation.ShadowDom,
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false
})
export class AlertFormComponent implements OnInit {

  @Input() price: number;
  @Input() directOnly: boolean = false;
  @Input() flexDates: boolean = false;
  @Input() isUpdateForm: boolean = false;
  @Input() hiddenFlexDate: boolean = false;
  @Input() flightType: FlightType;
  @Input() arrivalPlace: string;
  @Input() departureDate: Date;
  @Input() arrivalDate: Date;

  @Output() createAlert: EventEmitter<AlertSelectParams> = new EventEmitter<AlertSelectParams>();
  @Output() toggleConfirmFormEvent: EventEmitter<void> = new EventEmitter<void>();

  public priceForm: UntypedFormGroup;
  public priceItemForm: string = 'PRICE';
  public isSubmitted: boolean = false;

  public readonly alignCheckboxes = CheckboxAlign;

  private initialValues: Readonly<{
    directOnly: boolean,
    flexDates: boolean
  }>;

  private EDIT_LABEL_BUTTON: string = 'GENERAL_WIDGET_MODULE.ALERTS.CONFIRM.EDIT';
  private SAVE_LABEL_BUTTON: string = 'GENERAL_WIDGET_MODULE.ALERTS.CONFIRM.SAVE';
  protected AvoDispatch = new AvoDispatchOrchestrator();

  constructor(
    private formValidationService: FormValidationService,
    private dateFormatPipe: DateTransformPipe,
    private datePipe: DatePipe,
    private readonly avoService: AvoService,
    private readonly amplitude: PropertiesAmplitudeService,
    @Inject(IS_MOBILE) public isMobile: boolean
  ) { }

  ngOnInit(): void {
    this.createPriceForm();
    this.initialValues = { directOnly: this.directOnly, flexDates: this.flexDates };
  }

  get createOrUpdateTitle(): string {
    return this.isUpdateForm ? 'UPDATE_TITLE' : 'TITLE';
  }

  get customized(): boolean {
    if (this.directOnly !== this.initialValues.directOnly) return true;
    if (this.flexDates !== this.initialValues.flexDates) return true;
    if (this.priceForm.dirty) return this.priceForm.dirty;
    return false;
  }

  get buttonLabel(): string {
    return this.isUpdateForm ? this.EDIT_LABEL_BUTTON : this.SAVE_LABEL_BUTTON;
  }

  get tripDates(): string {
    const formatedDate = (date: Date) => this.dateFormatPipe.transform(this.datePipe.transform(date, 'dd MMM'));
    return this.flightType === FlightType.ONEWAY ?
      `${formatedDate(this.departureDate)}` :
      `${formatedDate(this.departureDate)} - ${formatedDate(this.arrivalDate)}`;
  }

  public createAlertEvent(): void {
    this.isSubmitted = true;
    if (!this.formInvalidControls(this.priceForm)) {
      this.createAlert.emit({
        maxPriceAlert: this.priceForm.get(this.priceItemForm).value,
        directOnly: this.directOnly,
        flexDates: this.flexDates
      });
      this.registerCreateTravelAlertEvent();
    }
  }

  public toggleConfirmForm(): void {
    this.toggleConfirmFormEvent.emit();
  }

  public setDirectOnly(): void {
    this.directOnly = !this.directOnly;
  }

  public setFlexDates(): void {
    this.flexDates = !this.flexDates;
  }

  private createPriceForm(): void {
    this.priceForm = new UntypedFormGroup({
      [this.priceItemForm]: new UntypedFormControl(
        this.price, [
          Validators.required,
          Validators.minLength(6),
          Validators.maxLength(10),
          Validators.max(this.price),
          Validators.min(1)
        ])
    });
  }

  private formInvalidControls(formValidation: UntypedFormGroup): boolean {
    return this.formValidationService.formInvalidControls(formValidation);
  }

  private registerCreateTravelAlertEvent(): void {
    this.amplitude.searchProperties$(SearchBy.FLIGHTS).
      pipe(
        take(1),
        switchMap(searchProps => this.avoService.getEventTriggerProps$(AVO_EVENTS.CREATE_TRAVEL_ALERT, null, searchProps)),
        tap(trigger => {
          this.AvoDispatch.createTravelAlert({
            ...this.avoService.avoBaseProperties(trigger),
            customized: this.customized
          });
        }
        )
      ).subscribe().unsubscribe();
  }
}
